import { Card, CardContent } from '@/components/ui/Card';
import { useGetVisionMonitorMetrics } from '@/hooks/useGetVisionMonitorMetrics';
import {
  VisionMonitorFilter,
  VisionMonitorScope,
} from '@/services/VisionMonitorService';

interface MetricsCardsProps {
  scope: VisionMonitorScope;
  onQuickFilter: (filter: VisionMonitorFilter) => void;
}

interface Stat {
  label: string;
  value?: number;
  // When set, clicking the stat narrows the table to that slice.
  filter?: VisionMonitorFilter;
}

const inFilter = (field: string, value: string): VisionMonitorFilter => ({
  field,
  operator: 'in',
  value: [value],
});

const isFilter = (
  field: string,
  value: 'true' | 'false',
): VisionMonitorFilter => ({
  field,
  operator: 'is',
  value,
});

export function MetricsCards({ scope, onQuickFilter }: MetricsCardsProps) {
  const { metrics, isLoading } = useGetVisionMonitorMetrics(scope);

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
      <StatCard
        title="Products (SKU rows)"
        total={metrics?.total}
        showTotal
        isLoading={isLoading}
        stats={[
          {
            label: 'Need run',
            value: metrics?.flags.needVisionRun,
            filter: isFilter('needVisionRun', 'true'),
          },
          {
            label: 'Sent',
            value: metrics?.flags.sentToVision,
            filter: isFilter('sentToVision', 'true'),
          },
          {
            label: 'Run',
            value: metrics?.flags.visionRun,
            filter: isFilter('visionRun', 'true'),
          },
        ]}
        onQuickFilter={onQuickFilter}
      />
      <StatCard
        title="Vision category"
        isLoading={isLoading}
        stats={[
          {
            label: 'Pending',
            value: metrics?.category.pending,
            filter: inFilter('visionCategoryStatus', 'PENDING'),
          },
          {
            label: 'Success',
            value: metrics?.category.success,
            filter: inFilter('visionCategoryStatus', 'SUCCESS'),
          },
          {
            label: 'Error',
            value: metrics?.category.error,
            filter: inFilter('visionCategoryStatus', 'ERROR'),
          },
        ]}
        onQuickFilter={onQuickFilter}
      />
      <StatCard
        title="Vision read"
        isLoading={isLoading}
        stats={[
          {
            label: 'Pending',
            value: metrics?.vision.pending,
            filter: inFilter('visionStatus', 'pending'),
          },
          {
            label: 'Success',
            value: metrics?.vision.success,
            filter: inFilter('visionStatus', 'success'),
          },
          {
            label: 'Error',
            value: metrics?.vision.error,
            filter: inFilter('visionStatus', 'error'),
          },
          {
            label: 'Synced',
            value: metrics?.vision.synced,
            filter: inFilter('visionStatus', 'synced'),
          },
        ]}
        onQuickFilter={onQuickFilter}
      />
      <StatCard
        title="Catalog criteria"
        isLoading={isLoading}
        stats={[
          {
            label: 'In stock',
            value: metrics?.catalog.inStock,
            filter: isFilter('stock', 'true'),
          },
          {
            label: 'No stock',
            value: metrics?.catalog.outOfStock,
            filter: isFilter('stock', 'false'),
          },
          {
            label: 'Active',
            value: metrics?.catalog.active,
            filter: isFilter('isActive', 'true'),
          },
          {
            label: 'Inactive',
            value: metrics?.catalog.inactive,
            filter: isFilter('isActive', 'false'),
          },
        ]}
        onQuickFilter={onQuickFilter}
      />
      <StatCard
        title="SEO"
        isLoading={isLoading}
        stats={[
          {
            label: 'Pending',
            value: metrics?.seo.pending,
            filter: inFilter('seoStatus', 'PENDING'),
          },
          {
            label: 'Success',
            value: metrics?.seo.success,
            filter: inFilter('seoStatus', 'SUCCESS'),
          },
          {
            label: 'Error',
            value: metrics?.seo.error,
            filter: inFilter('seoStatus', 'ERROR'),
          },
          {
            label: 'Synced',
            value: metrics?.seo.synced,
            filter: isFilter('seoSynced', 'true'),
          },
        ]}
        onQuickFilter={onQuickFilter}
      />
      <StatCard
        className="md:col-span-2 xl:col-span-5"
        title="Pending vision pipeline"
        isLoading={isLoading}
        stats={[
          {
            label: 'Waiting for category',
            value: metrics?.pipeline.waitingCategory,
          },
          {
            label: 'Queued to send to Vision',
            value: metrics?.pipeline.queuedForVision,
          },
          {
            label: 'Sent, waiting for result',
            value: metrics?.pipeline.inVision,
          },
          {
            label: 'Blocked by stock / active',
            value: metrics?.pipeline.blockedByStockOrActive,
          },
        ]}
        onQuickFilter={onQuickFilter}
      />
    </div>
  );
}

interface StatCardProps {
  title: string;
  total?: number;
  showTotal?: boolean;
  stats: Stat[];
  isLoading: boolean;
  className?: string;
  onQuickFilter: (filter: VisionMonitorFilter) => void;
}

function StatCard({
  title,
  total,
  showTotal,
  stats,
  isLoading,
  className,
  onQuickFilter,
}: StatCardProps) {
  const format = (value?: number) =>
    isLoading && value === undefined ? '…' : (value ?? 0).toLocaleString();

  return (
    <Card className={className}>
      <CardContent className="space-y-3 p-4">
        <p className="text-sm font-medium text-muted-foreground">{title}</p>

        {showTotal && <p className="text-2xl font-bold">{format(total)}</p>}

        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {stats.map(({ label, value, filter }) => {
            const content = (
              <>
                <span className="block text-lg font-semibold leading-tight">
                  {format(value)}
                </span>
                <span className="text-xs text-muted-foreground">{label}</span>
              </>
            );

            return filter ? (
              <button
                key={label}
                type="button"
                title={`Filter by: ${label}`}
                className="rounded text-left transition-colors hover:bg-gray-100"
                onClick={() => onQuickFilter(filter)}
              >
                {content}
              </button>
            ) : (
              <div key={label}>{content}</div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
